import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { translations, Language, TranslationKey, TranslationSubKey } from '../i18n/translations';

interface LanguageContextType {
    language: Language;
    setLanguage: (lang: Language) => void;
    t: (section: TranslationKey, key: string) => string;
    toggleLanguage: () => void;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const STORAGE_KEY = 'dacams_language';

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    // Initialize from localStorage or default to Spanish
    const [language, setLanguageState] = useState<Language>(() => {
        const stored = localStorage.getItem(STORAGE_KEY);
        return (stored === 'en' || stored === 'es') ? stored : 'es';
    });

    // Persist to localStorage when language changes
    useEffect(() => {
        localStorage.setItem(STORAGE_KEY, language);
    }, [language]);

    const setLanguage = useCallback((lang: Language) => {
        setLanguageState(lang);
    }, []);

    const toggleLanguage = useCallback(() => {
        setLanguageState(prev => prev === 'es' ? 'en' : 'es');
    }, []);

    // Translation function
    const t = useCallback((section: TranslationKey, key: string): string => {
        try {
            const sectionData = translations[section];
            if (sectionData && key in sectionData) {
                const entry = (sectionData as Record<string, Record<Language, string>>)[key];
                return entry[language] || key;
            }
            return key;
        } catch {
            return key;
        }
    }, [language]);

    return (
        <LanguageContext.Provider value={{ language, setLanguage, t, toggleLanguage }}>
            {children}
        </LanguageContext.Provider>
    );
};

// Hook to use language context
export const useLanguage = (): LanguageContextType => {
    const context = useContext(LanguageContext);
    if (!context) {
        throw new Error('useLanguage must be used within a LanguageProvider');
    }
    return context;
};

// Export types
export type { Language };
